import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { apiHelpers } from '../../services/api';
import toast from 'react-hot-toast';
import { useCreditCosts } from '../../hooks/useCreditCosts';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  TrashIcon,
  ClockIcon,
  TableCellsIcon,
  SparklesIcon,
  TagIcon,
  DocumentTextIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  LightBulbIcon,
  ChartBarIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import SyncScheduleEditor from '../../components/datasource/SyncScheduleEditor';
import SchemaEditor from '../../components/datasource/SchemaEditor';
import { useAuthStore } from '../../store/authStore';
import { useDropzone } from 'react-dropzone';
import { CloudArrowUpIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { translateSyncError, getErrorStyles } from '../../utils/syncErrorHelper';

export default function DataSourceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getCurrentCompany, refreshCompany } = useAuthStore();
  const company = getCurrentCompany();
  const companyTimezone = company?.settings?.defaultTimezone || 'UTC';
  // Use subscription plan's slug if available, otherwise fall back to billingPlan
  const companyPlan = company?.subscriptionPlanId?.slug || company?.billingPlan || 'starter';
  const { formatCost } = useCreditCosts();
  
  // Debug: Log company data
  console.log('[DataSourceDetail] Company data:', {
    _id: company?._id,
    name: company?.name,
    billingPlan: company?.billingPlan,
    subscriptionPlanId: company?.subscriptionPlanId,
    resolvedPlan: companyPlan
  });
  
  // Refresh company data on mount to ensure we have the latest billing plan info
  useEffect(() => {
    console.log('[DataSourceDetail] Refreshing company data...');
    refreshCompany().then(result => {
      console.log('[DataSourceDetail] Refresh result:', result);
    });
  }, [refreshCompany]);
  
  // Editing states
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingTags, setIsEditingTags] = useState(false);
  const [isEditingContext, setIsEditingContext] = useState(false);
  const [editDescription, setEditDescription] = useState('');
  const [editTags, setEditTags] = useState('');
  const [editContext, setEditContext] = useState('');
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  
  // Data preview states
  const [showDataPreview, setShowDataPreview] = useState(false);
  const [previewOffset, setPreviewOffset] = useState(0);
  const previewLimit = 50;
  
  // Schedule editor states
  const [showScheduleEditor, setShowScheduleEditor] = useState(false);
  
  // Schema editor states
  const [showSchemaEditor, setShowSchemaEditor] = useState(false);
  
  // Sync progress states
  const [currentImportId, setCurrentImportId] = useState(null);
  const [syncProgress, setSyncProgress] = useState(null);
  const [clearingJobs, setClearingJobs] = useState(false);
  
  // Column description editing
  const [editingColumnDesc, setEditingColumnDesc] = useState(null); // column name being edited
  const [editColumnDescValue, setEditColumnDescValue] = useState('');
  const [savingColumnDesc, setSavingColumnDesc] = useState(false);
  
  // Re-upload file dialog for file-based sources (CSV/Excel)
  const [showReuploadDialog, setShowReuploadDialog] = useState(false);

  // AI Research re-generation confirmation
  const [showAiResearchConfirm, setShowAiResearchConfirm] = useState(false);
  
  // Deduplication states
  const [showDeduplicateModal, setShowDeduplicateModal] = useState(false);
  const [deduplicateKeyColumns, setDeduplicateKeyColumns] = useState([]);
  
  // Web scraper editing states
  const [isEditingWebScraper, setIsEditingWebScraper] = useState(false);
  const [editScrapeUrl, setEditScrapeUrl] = useState('');
  const [editScrapePrompt, setEditScrapePrompt] = useState('');
  const [editPaginationStrategy, setEditPaginationStrategy] = useState('single');
  const [editPaginationDepth, setEditPaginationDepth] = useState(1);
  const [editUrlPattern, setEditUrlPattern] = useState('');
  const [savingWebScraper, setSavingWebScraper] = useState(false);
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['dataSource', id],
    queryFn: () => apiHelpers.getDataSource(id)
  });
  
  const { data: schemaData } = useQuery({
    queryKey: ['dataSourceSchema', id],
    queryFn: () => apiHelpers.getDataSourceSchema(id)
  });
  
  const { data: previewData, isLoading: isLoadingPreview, refetch: refetchPreview } = useQuery({
    queryKey: ['dataSourcePreview', id, previewOffset],
    queryFn: () => apiHelpers.getDataSourceData(id, { limit: previewLimit, offset: previewOffset }),
    enabled: showDataPreview
  });
  
  // Fetch daily sync limit info
  const { data: syncLimitData } = useQuery({
    queryKey: ['syncLimit', id],
    queryFn: async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const response = await apiHelpers.getImportHistory(id, { 
        limit: 1000,
        triggerType: 'manual'
      });
      
      const importsToday = response.data?.data?.filter(imp => 
        new Date(imp.createdAt) >= today
      ) || [];
      
      return {
        used: importsToday.length,
        planLimit: company?.subscriptionPlanId?.features?.manualSyncLimitPerDay || 3
      };
    },
    refetchInterval: 60000, // Refresh every minute
    enabled: !!id
  });
  
  // Check for ongoing imports when page loads
  useEffect(() => {
    const checkOngoingImport = async () => {
      try {
        const response = await api.get(`/data-sources/${id}/imports`, { params: { limit: 1 } });
        const imports = response.data?.data || [];
        
        if (imports.length > 0) {
          const latestImport = imports[0];
          // If the most recent import is running or pending, start tracking it
          if (['running', 'pending'].includes(latestImport.status)) {
            setCurrentImportId(latestImport._id);
            setSyncProgress({
              status: latestImport.status,
              phase: latestImport.progress?.phase || 'extracting',
              percentage: latestImport.progress?.percentage || 0,
              message: latestImport.progress?.message || 'Import in progress...'
            });
          }
        }
      } catch (error) {
        console.error('Error checking for ongoing imports:', error);
      }
    };
    
    if (id && !currentImportId) {
      checkOngoingImport();
    }
  }, [id]); // Only run on initial load
  
  const importMutation = useMutation({
    mutationFn: (mode) => apiHelpers.importData(id, mode),
    onSuccess: (response) => {
      const importId = response.data?.import?._id;
      const dailyLimitInfo = response.data?.dailyLimitInfo;
      
      if (importId) {
        setCurrentImportId(importId);
        setSyncProgress({
          status: 'running',
          phase: 'extracting',
          percentage: 0,
          message: 'Starting import...'
        });
        
        // Show success with limit info if available
        if (dailyLimitInfo) {
          toast.success(`Import started (${dailyLimitInfo.remaining} syncs remaining today)`);
        } else {
          toast.success('Import started - tracking progress...');
        }
      } else {
        toast.success('Import started');
      }
    },
    onError: (error) => {
      // Handle daily limit exceeded
      if (error.response?.status === 429) {
        const errorData = error.response?.data;
        const resetTime = errorData?.resetAt ? new Date(errorData.resetAt).toLocaleTimeString() : 'midnight';
        toast.error(
          `Daily sync limit reached (${errorData?.used}/${errorData?.limit}). Resets at ${resetTime}.`,
          { duration: 6000 }
        );
      }
      // Handle the case where an import is already in progress
      else if (error.response?.data?.error?.includes('already running')) {
        toast.error('An import is already in progress for this data source');
      } else {
        toast.error(error.response?.data?.error || 'Failed to start import');
      }
    }
  });
  
  // Poll for import progress when we have an active import
  useEffect(() => {
    if (!currentImportId) return;
    
    console.log('[DataSourceDetail] Starting progress polling for import:', currentImportId);
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await apiHelpers.getImportStatus(currentImportId);
        const { import: importData, dataSource: updatedDataSource } = response.data;
        
        console.log('[DataSourceDetail] Poll result:', {
          status: importData.status,
          phase: importData.progress?.phase,
          percentage: importData.progress?.percentage
        });
        
        setSyncProgress({
          status: importData.status,
          phase: importData.progress?.phase || 'extracting',
          percentage: importData.progress?.percentage || 0,
          message: importData.progress?.message || '',
          error: importData.error
        });
        
        // Check if import is complete or failed
        if (importData.status === 'completed') {
          clearInterval(pollInterval);
          setCurrentImportId(null);
          toast.success('Import completed successfully!');
          
          // Refresh the page data
          queryClient.invalidateQueries(['dataSource', id]);
          queryClient.invalidateQueries(['dataSourceSchema', id]);
          queryClient.invalidateQueries(['dataSourcePreview', id]);
          refetch();
          
          // Keep showing success state briefly before clearing
          setTimeout(() => {
            setSyncProgress(null);
          }, 3000);
        } else if (importData.status === 'failed') {
          clearInterval(pollInterval);
          setCurrentImportId(null);
          
          // Check for specific error types and show helpful messages
          const errorCode = importData.error?.code;
          const errorMessage = importData.error?.message || 'Unknown error';
          
          if (errorCode === 'FILE_DATA_EXPIRED' || errorMessage.includes('expired') || errorMessage.includes('re-upload')) {
            toast.error(
              'File data has expired. Please re-upload your file using the "Re-upload" button below.',
              { duration: 8000 }
            );
          } else if (errorCode === 'BIGQUERY_SCHEMA_MISMATCH' || errorCode === 'BIGQUERY_LOAD_FAILED' || 
                     errorMessage.includes('schema') || errorMessage.includes('Could not convert')) {
            // BigQuery schema/type mismatch - re-syncing should auto-fix
            toast.error(
              'Schema mismatch detected. The data types in your source don\'t match the existing table. Click "Sync Now" again to auto-fix.',
              { duration: 10000 }
            );
          } else {
            toast.error(`Import failed: ${errorMessage}`);
          }
          
          // Keep showing error state briefly before clearing
          setTimeout(() => {
            setSyncProgress(null);
          }, 5000);
        }
      } catch (error) {
        console.error('Error polling import status:', error);
      }
    }, 2000); // Poll every 2 seconds
    
    return () => clearInterval(pollInterval);
  }, [currentImportId, id, queryClient, refetch]);
  
  const deleteMutation = useMutation({
    mutationFn: () => apiHelpers.deleteDataSource(id),
    onSuccess: () => {
      toast.success('Data source deleted');
      // Invalidate the data sources list cache so the list refreshes
      queryClient.invalidateQueries(['dataSources']);
      navigate('/data');
    },
    onError: () => {
      toast.error('Failed to delete data source');
    }
  });
  
  // Handle clearing stuck import jobs
  const handleClearJobs = async () => {
    if (!window.confirm('This will cancel any pending or stuck import jobs for this data source. Continue?')) {
      return;
    }
    
    setClearingJobs(true);
    try {
      const response = await api.post(`/data-sources/${id}/clear-jobs`);
      const cleared = response.data?.cleared || 0;
      if (cleared > 0) {
        toast.success(`Cleared ${cleared} pending job(s)`);
      } else {
        toast.success('No pending jobs to clear');
      }
      // Reset sync progress state
      setCurrentImportId(null);
      setSyncProgress(null);
    } catch (error) {
      console.error('Clear jobs error:', error);
      toast.error('Failed to clear jobs');
    } finally {
      setClearingJobs(false);
    }
  };
  
  const scheduleMutation = useMutation({
    mutationFn: (schedule) => apiHelpers.updateSchedule(id, schedule),
    onSuccess: (result) => {
      toast.success('Sync schedule updated');
      setShowScheduleEditor(false);
      queryClient.invalidateQueries(['dataSource', id]);
      refetch(); // Explicitly refetch to ensure we have the latest data
      if (result.data?.nextRunAt) {
        toast.success(`Next sync scheduled for: ${new Date(result.data.nextRunAt).toLocaleString()}`);
      }
    },
    onError: () => {
      toast.error('Failed to update schedule');
    }
  });
  
  const schemaMutation = useMutation({
    mutationFn: (updates) => api.put(`/data-sources/${id}`, updates),
    onSuccess: () => {
      toast.success('Schema updated! Sync the data source to apply changes.');
      setShowSchemaEditor(false);
      queryClient.invalidateQueries(['dataSource', id]);
      queryClient.invalidateQueries(['dataSourceSchema', id]);
      refetch();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update schema');
    }
  });
  
  const deduplicateMutation = useMutation({
    mutationFn: (keyColumns) => apiHelpers.deduplicateData(id, keyColumns),
    onSuccess: (response) => {
      const { rowsBefore, rowsAfter, rowsRemoved } = response.data;
      toast.success(`Removed ${rowsRemoved.toLocaleString()} duplicate rows (${rowsBefore.toLocaleString()} → ${rowsAfter.toLocaleString()})`);
      setShowDeduplicateModal(false);
      setDeduplicateKeyColumns([]);
      queryClient.invalidateQueries(['dataSource', id]);
      queryClient.invalidateQueries(['dataSourcePreview', id]);
      refetch();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to deduplicate data');
    }
  });
  
  const dataSource = data?.data?.dataSource;
  const schema = schemaData?.data?.schema;
  
  // Check if this is a file-based data source (CSV/Excel) vs external source (API, DB, etc.)
  const isFileSource = dataSource && ['csv', 'excel'].includes(dataSource.type);
  const isAiResearch = dataSource && dataSource.type === 'airesearch';
  const hasExistingData = dataSource?.stats?.totalRows > 0;
  
  const handleSaveMetadata = async (field) => {
    try {
      const updates = {};
      if (field === 'description') {
        updates.description = editDescription;
        setIsEditingDescription(false);
      } else if (field === 'tags') {
        updates.tags = editTags.split(',').map(t => t.trim()).filter(t => t);
        setIsEditingTags(false);
      } else if (field === 'businessContext') {
        updates.businessContext = editContext;
        setIsEditingContext(false);
      }
      
      await api.patch(`/data-sources/${id}/metadata`, updates);
      toast.success('Saved successfully');
      refetch();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save');
    }
  };
  
  const handleStartEditWebScraper = () => {
    setEditScrapeUrl(dataSource.connectionConfig?.scrapeUrl || '');
    setEditScrapePrompt(dataSource.connectionConfig?.scrapePrompt || '');
    setEditPaginationStrategy(dataSource.connectionConfig?.paginationStrategy || 'single');
    setEditPaginationDepth(dataSource.connectionConfig?.paginationDepth || 1);
    setEditUrlPattern(dataSource.connectionConfig?.urlPattern || '');
    setIsEditingWebScraper(true);
  };

  const handleSaveWebScraper = async () => {
    setSavingWebScraper(true);
    try {
      await apiHelpers.updateDataSource(id, {
        connectionConfig: {
          scrapeUrl: editScrapeUrl,
          scrapePrompt: editScrapePrompt,
          paginationStrategy: editPaginationStrategy,
          paginationDepth: editPaginationDepth,
          urlPattern: editPaginationStrategy === 'url_pattern' ? editUrlPattern : undefined
        }
      });
      toast.success('Web scraper settings saved');
      setIsEditingWebScraper(false);
      refetch();
    } catch (error) {
      console.error('Save web scraper settings error:', error);
      toast.error('Failed to save settings');
    } finally {
      setSavingWebScraper(false);
    }
  };

  const handleGenerateAIContext = async () => {
    setIsGeneratingAI(true);
    try {
      const response = await apiHelpers.generateDataSourceAIContext(id);
      toast.success('AI description generated!');
      refetch();
      
      // Show the generated content
      if (response.data.aiContext) {
        console.log('AI Context:', response.data.aiContext);
      }
    } catch (error) {
      console.error('Generate AI context error:', error);
      toast.error(error.response?.data?.error || 'Failed to generate AI description');
    } finally {
      setIsGeneratingAI(false);
    }
  };
  
  // Start editing a column description
  const startEditingColumnDesc = (columnName, currentDesc) => {
    setEditingColumnDesc(columnName);
    setEditColumnDescValue(currentDesc || '');
  };
  
  // Save column description
  const saveColumnDescription = async () => {
    if (!editingColumnDesc) return;
    
    setSavingColumnDesc(true);
    try {
      await api.patch(`/data-sources/${id}/metadata`, {
        columnDescriptions: {
          [editingColumnDesc]: editColumnDescValue
        }
      });
      toast.success('Column description saved');
      setEditingColumnDesc(null);
      setEditColumnDescValue('');
      refetch();
    } catch (error) {
      console.error('Save column description error:', error);
      toast.error('Failed to save column description');
    } finally {
      setSavingColumnDesc(false);
    }
  };
  
  // Cancel editing column description
  const cancelEditingColumnDesc = () => {
    setEditingColumnDesc(null);
    setEditColumnDescValue('');
  };
  
  const startEditing = (field) => {
    if (field === 'description') {
      setEditDescription(dataSource.description || '');
      setIsEditingDescription(true);
    } else if (field === 'tags') {
      setEditTags((dataSource.tags || []).join(', '));
      setIsEditingTags(true);
    } else if (field === 'businessContext') {
      setEditContext(dataSource.businessContext || '');
      setIsEditingContext(true);
    }
  };
  
  if (isLoading) {
    return (
      <div className="animate-fade-in">
        <div className="skeleton h-8 w-48 mb-8" />
        <div className="card p-6">
          <div className="skeleton h-6 w-64 mb-4" />
          <div className="skeleton h-4 w-full mb-2" />
          <div className="skeleton h-4 w-3/4" />
        </div>
      </div>
    );
  }
  
  if (!dataSource) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-medium text-surface-900 dark:text-white mb-2">
          Data source not found
        </h2>
        <button onClick={() => navigate('/data')} className="btn-primary mt-4">
          Back to Data Sources
        </button>
      </div>
    );
  }
  
  const aiContext = dataSource.aiContext;
  
  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/data')}
            className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
          >
            <ArrowLeftIcon className="w-5 h-5 text-surface-600 dark:text-surface-400" />
          </button>
          <div>
            <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
              {dataSource.name}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="badge-primary">{dataSource.type.toUpperCase()}</span>
              <span className={`badge ${
                dataSource.status === 'ready' ? 'badge-success' : 
                dataSource.status === 'error' ? 'badge-danger' : 'badge-warning'
              }`}>
                {dataSource.status}
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Daily Sync Limit Indicator */}
          {syncLimitData && !isAiResearch && (
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-surface-100 dark:bg-surface-800 rounded-lg border border-surface-200 dark:border-surface-700">
              <ClockIcon className="w-4 h-4 text-surface-500" />
              <span className="text-xs text-surface-600 dark:text-surface-400">
                {syncLimitData.planLimit - syncLimitData.used} / {syncLimitData.planLimit} syncs left today
              </span>
            </div>
          )}
          
          {/* Only show Schedule button for external sources (not CSV/Excel or AI Research) */}
          {!isFileSource && !isAiResearch && (
            <button
              onClick={() => setShowScheduleEditor(true)}
              className="btn-secondary"
            >
              <Cog6ToothIcon className="w-5 h-5 mr-2" />
              Schedule
            </button>
          )}
          
          {/* For file sources: always show re-upload dialog since Redis cache expires after 2 hours */}
          {isFileSource ? (
            <button
              onClick={() => setShowReuploadDialog(true)}
              disabled={importMutation.isPending || !!currentImportId || (syncLimitData && syncLimitData.used >= syncLimitData.planLimit)}
              className="btn-primary disabled:opacity-50"
              title={syncLimitData && syncLimitData.used >= syncLimitData.planLimit ? 'Daily sync limit reached' : ''}
            >
              <ArrowPathIcon className={`w-5 h-5 mr-2 ${(importMutation.isPending || currentImportId) ? 'animate-spin' : ''}`} />
              {currentImportId ? 'Importing...' : 'Import Data'}
            </button>
          ) : isAiResearch ? (
            <button
              onClick={() => setShowAiResearchConfirm(true)}
              disabled={importMutation.isPending || !!currentImportId}
              className="btn-primary disabled:opacity-50"
            >
              <ArrowPathIcon className={`w-5 h-5 mr-2 ${(importMutation.isPending || currentImportId) ? 'animate-spin' : ''}`} />
              {currentImportId ? 'Regenerating...' : 'Regenerate Data'}
            </button>
          ) : (
            <button
              onClick={() => importMutation.mutate(dataSource.schedule?.importMode || 'full')}
              disabled={importMutation.isPending || !!currentImportId || (syncLimitData && syncLimitData.used >= syncLimitData.planLimit)}
              className="btn-primary disabled:opacity-50"
              title={syncLimitData && syncLimitData.used >= syncLimitData.planLimit ? 'Daily sync limit reached' : ''}
            >
              <ArrowPathIcon className={`w-5 h-5 mr-2 ${(importMutation.isPending || currentImportId) ? 'animate-spin' : ''}`} />
              {currentImportId ? 'Syncing...' : 'Sync Now'}
            </button>
          )}
          
          {/* Show Clear Jobs button when sync seems stuck */}
          {(currentImportId || syncProgress?.status === 'running') && (
            <button
              onClick={handleClearJobs}
              disabled={clearingJobs}
              className="btn-secondary text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
              title="Cancel pending import jobs"
            >
              <XMarkIcon className={`w-5 h-5 mr-2 ${clearingJobs ? 'animate-spin' : ''}`} />
              {clearingJobs ? 'Clearing...' : 'Clear Jobs'}
            </button>
          )}
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to delete this data source?')) {
                deleteMutation.mutate();
              }
            }}
            className="btn-danger"
          >
            <TrashIcon className="w-5 h-5 mr-2" />
            Delete
          </button>
        </div>
      </div>
      
      {/* Import/Sync Progress Bar */}
      {syncProgress && (
        <div className="mb-6 card p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {syncProgress.status === 'completed' ? (
                <CheckCircleIcon className="w-5 h-5 text-green-500" />
              ) : syncProgress.status === 'failed' ? (
                <ExclamationCircleIcon className="w-5 h-5 text-red-500" />
              ) : (
                <ArrowPathIcon className="w-5 h-5 text-primary-500 animate-spin" />
              )}
              <span className="font-medium text-surface-900 dark:text-white">
                {syncProgress.status === 'completed' ? (isFileSource ? 'Import Complete' : 'Sync Complete') :
                 syncProgress.status === 'failed' ? (isFileSource ? 'Import Failed' : 'Sync Failed') :
                 `${isFileSource ? 'Importing' : 'Syncing'}: ${syncProgress.phase || 'Starting'}...`}
              </span>
            </div>
            <span className="text-sm text-surface-500">
              {syncProgress.percentage}%
            </span>
          </div>
          
          {/* Progress bar */}
          <div className="w-full h-2 bg-surface-200 dark:bg-surface-700 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-500 rounded-full ${
                syncProgress.status === 'completed' ? 'bg-green-500' :
                syncProgress.status === 'failed' ? 'bg-red-500' :
                'bg-primary-500'
              }`}
              style={{ width: `${syncProgress.percentage}%` }}
            />
          </div>
          
          {/* Status message */}
          {syncProgress.message && (
            <p className="text-sm text-surface-500 dark:text-surface-400 mt-2">
              {syncProgress.message}
            </p>
          )}
          
          {/* Error message with translation */}
          {syncProgress.status === 'failed' && syncProgress.error && (() => {
            const translatedError = translateSyncError(syncProgress.error);
            const styles = getErrorStyles(translatedError.type);
            return (
              <div className={`mt-3 p-3 rounded-lg border ${styles.bg} ${styles.border}`}>
                <div className="flex items-start gap-2">
                  <InformationCircleIcon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${styles.icon}`} />
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-medium text-sm ${styles.title}`}>{translatedError.title}</h4>
                    <p className={`text-sm mt-1 ${styles.text}`}>{translatedError.message}</p>
                    {translatedError.suggestion && (
                      <p className={`text-sm mt-2 ${styles.text} opacity-80`}>
                        💡 {translatedError.suggestion}
                      </p>
                    )}
                    {translatedError.originalMessage && translatedError.originalMessage !== translatedError.message && (
                      <details className="mt-2">
                        <summary className={`text-xs cursor-pointer ${styles.text} opacity-60 hover:opacity-80`}>
                          Show technical details
                        </summary>
                        <code className={`text-xs mt-1 block p-2 rounded bg-black/10 dark:bg-white/10 ${styles.text}`}>
                          {translatedError.originalCode && `[${translatedError.originalCode}] `}
                          {translatedError.originalMessage}
                        </code>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
          
          {/* Help link for stuck imports/syncs */}
          {syncProgress.status === 'running' && (
            <p className="text-xs text-surface-400 mt-2">
              {isFileSource ? 'Import' : 'Sync'} taking too long?{' '}
              <button 
                onClick={handleClearJobs}
                className="text-amber-600 dark:text-amber-400 hover:underline"
              >
                Clear pending jobs
              </button>
            </p>
          )}
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="card p-4">
              <p className="text-sm text-surface-500 dark:text-surface-400 mb-1">Total Rows</p>
              <p className="text-2xl font-display font-bold text-surface-900 dark:text-white">
                {dataSource.stats?.totalRows?.toLocaleString() || 0}
              </p>
            </div>
            <div className="card p-4">
              <p className="text-sm text-surface-500 dark:text-surface-400 mb-1">Storage</p>
              <p className="text-2xl font-display font-bold text-surface-900 dark:text-white">
                {formatBytes(dataSource.stats?.totalSizeBytes || 0)}
              </p>
            </div>
            <div className="card p-4">
              <p className="text-sm text-surface-500 dark:text-surface-400 mb-1">Imports</p>
              <p className="text-2xl font-display font-bold text-surface-900 dark:text-white">
                {dataSource.stats?.importCount || 0}
              </p>
            </div>
          </div>
          
          {/* AI Context Card */}
          <div className="card">
            <div className="p-4 border-b border-surface-200 dark:border-surface-800 flex items-center justify-between">
              <h2 className="font-medium text-surface-900 dark:text-white flex items-center gap-2">
                <SparklesIcon className="w-5 h-5 text-primary-500" />
                AI Description & Context
              </h2>
              <button
                onClick={handleGenerateAIContext}
                disabled={isGeneratingAI}
                className="btn-primary text-sm"
                title={`${aiContext?.generatedDescription ? 'Regenerate' : 'Generate'} AI description ${formatCost('data_source_describe')}`}
              >
                {isGeneratingAI ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="w-4 h-4 mr-2" />
                    {aiContext?.generatedDescription ? 'Regenerate' : 'Generate with AI'}
                    {formatCost('data_source_describe') && (
                      <span className="ml-1 text-xs opacity-75">{formatCost('data_source_describe')}</span>
                    )}
                  </>
                )}
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {aiContext?.generatedDescription ? (
                <>
                  <div>
                    <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                      What this data contains
                    </h4>
                    <p className="text-surface-600 dark:text-surface-400">
                      {aiContext.generatedDescription}
                    </p>
                  </div>
                  
                  {aiContext.suggestedUseCases?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-2 flex items-center gap-2">
                        <LightBulbIcon className="w-4 h-4" />
                        Suggested Use Cases
                      </h4>
                      <ul className="list-disc list-inside text-surface-600 dark:text-surface-400 space-y-1">
                        {aiContext.suggestedUseCases.map((useCase, i) => (
                          <li key={i}>{useCase}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {aiContext.detectedPatterns && (
                    <div>
                      <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-2 flex items-center gap-2">
                        <ChartBarIcon className="w-4 h-4" />
                        Detected Patterns
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {aiContext.detectedPatterns.hasTimeSeries && (
                          <span className="badge bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                            Time Series ({aiContext.detectedPatterns.timeColumn})
                          </span>
                        )}
                        {aiContext.detectedPatterns.hasMetrics && (
                          <span className="badge bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            Metrics ({aiContext.detectedPatterns.metricColumns?.length} columns)
                          </span>
                        )}
                        {aiContext.detectedPatterns.hasCategories && (
                          <span className="badge bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                            Categories ({aiContext.detectedPatterns.categoryColumns?.length} columns)
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-surface-400">
                    Last generated: {new Date(aiContext.lastGeneratedAt).toLocaleString()}
                  </p>
                </>
              ) : (
                <div className="text-center py-6 text-surface-500">
                  <SparklesIcon className="w-12 h-12 mx-auto mb-3 text-surface-300" />
                  <p className="mb-2">No AI description generated yet</p>
                  <p className="text-sm">Click "Generate with AI" to automatically create a description that helps the AI understand this data source</p>
                </div>
              )}
            </div>
          </div>
          
          {/* User Description & Context */}
          <div className="card">
            <div className="p-4 border-b border-surface-200 dark:border-surface-800">
              <h2 className="font-medium text-surface-900 dark:text-white flex items-center gap-2">
                <DocumentTextIcon className="w-5 h-5" />
                Description & Business Context
              </h2>
            </div>
            
            <div className="p-4 space-y-4">
              {/* User Description */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-surface-700 dark:text-surface-300">
                    Description
                  </label>
                  {!isEditingDescription && (
                    <button
                      onClick={() => startEditing('description')}
                      className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                    >
                      <PencilIcon className="w-3 h-3" />
                      Edit
                    </button>
                  )}
                </div>
                {isEditingDescription ? (
                  <div className="space-y-2">
                    <textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      className="input h-24 resize-none"
                      placeholder="Describe what this data source contains..."
                    />
                    <div className="flex gap-2">
                      <button onClick={() => handleSaveMetadata('description')} className="btn-primary text-sm">
                        <CheckIcon className="w-4 h-4 mr-1" />
                        Save
                      </button>
                      <button onClick={() => setIsEditingDescription(false)} className="btn-secondary text-sm">
                        <XMarkIcon className="w-4 h-4 mr-1" />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-surface-600 dark:text-surface-400">
                    {dataSource.description || 'No description provided'}
                  </p>
                )}
              </div>
              
              {/* Tags */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-surface-700 dark:text-surface-300 flex items-center gap-1">
                    <TagIcon className="w-4 h-4" />
                    Tags
                  </label>
                  {!isEditingTags && (
                    <button
                      onClick={() => startEditing('tags')}
                      className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                    >
                      <PencilIcon className="w-3 h-3" />
                      Edit
                    </button>
                  )}
                </div>
                {isEditingTags ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={editTags}
                      onChange={(e) => setEditTags(e.target.value)}
                      className="input"
                      placeholder="sales, marketing, revenue (comma-separated)"
                    />
                    <div className="flex gap-2">
                      <button onClick={() => handleSaveMetadata('tags')} className="btn-primary text-sm">
                        <CheckIcon className="w-4 h-4 mr-1" />
                        Save
                      </button>
                      <button onClick={() => setIsEditingTags(false)} className="btn-secondary text-sm">
                        <XMarkIcon className="w-4 h-4 mr-1" />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {dataSource.tags?.length > 0 ? (
                      dataSource.tags.map((tag, i) => (
                        <span key={i} className="badge bg-surface-100 dark:bg-surface-800 text-surface-700 dark:text-surface-300">
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="text-surface-500">No tags</span>
                    )}
                  </div>
                )}
              </div>
              
              {/* Business Context */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-surface-700 dark:text-surface-300">
                    Business Context
                  </label>
                  {!isEditingContext && (
                    <button
                      onClick={() => startEditing('businessContext')}
                      className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                    >
                      <PencilIcon className="w-3 h-3" />
                      Edit
                    </button>
                  )}
                </div>
                {isEditingContext ? (
                  <div className="space-y-2">
                    <textarea
                      value={editContext}
                      onChange={(e) => setEditContext(e.target.value)}
                      className="input h-24 resize-none"
                      placeholder="Add business context, domain-specific terms, or notes about data quality..."
                    />
                    <div className="flex gap-2">
                      <button onClick={() => handleSaveMetadata('businessContext')} className="btn-primary text-sm">
                        <CheckIcon className="w-4 h-4 mr-1" />
                        Save
                      </button>
                      <button onClick={() => setIsEditingContext(false)} className="btn-secondary text-sm">
                        <XMarkIcon className="w-4 h-4 mr-1" />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-surface-600 dark:text-surface-400">
                    {dataSource.businessContext || 'No business context provided'}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* Schema */}
          <div className="card">
            <div className="p-4 border-b border-surface-200 dark:border-surface-800 flex items-center justify-between">
              <h2 className="font-medium text-surface-900 dark:text-white flex items-center gap-2">
                <TableCellsIcon className="w-5 h-5" />
                Schema ({schema?.tables?.[0]?.columns?.length || 0} columns)
              </h2>
              <div className="flex items-center gap-2">
                {['postgresql', 'mysql', 'mongodb'].includes(dataSource.type) && (
                  <button
                    onClick={() => setShowSchemaEditor(true)}
                    className="btn-secondary text-sm"
                  >
                    <PencilIcon className="w-4 h-4 mr-2" />
                    Edit Schema
                  </button>
                )}
                {dataSource.status === 'ready' && (
                  <button
                    onClick={() => setShowDataPreview(!showDataPreview)}
                    className="btn-secondary text-sm"
                  >
                    <EyeIcon className="w-4 h-4 mr-2" />
                    {showDataPreview ? 'Hide Data' : 'View Data'}
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface-50 dark:bg-surface-800">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-surface-600 dark:text-surface-400">Column</th>
                    <th className="px-4 py-3 text-left font-medium text-surface-600 dark:text-surface-400">Type</th>
                    <th className="px-4 py-3 text-left font-medium text-surface-600 dark:text-surface-400">AI Description</th>
                  </tr>
                </thead>
                <tbody>
                  {schema?.tables?.[0]?.columns?.map((col, i) => (
                    <tr key={i} className="border-b border-surface-100 dark:border-surface-800">
                      <td className="px-4 py-3 text-surface-900 dark:text-white font-mono">{col.name}</td>
                      <td className="px-4 py-3">
                        <span className="badge-primary">{col.type}</span>
                      </td>
                      <td className="px-4 py-3 text-surface-600 dark:text-surface-400">
                        {editingColumnDesc === col.name ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editColumnDescValue}
                              onChange={(e) => setEditColumnDescValue(e.target.value)}
                              className="input text-sm flex-1 py-1"
                              placeholder="Enter description..."
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveColumnDescription();
                                if (e.key === 'Escape') cancelEditingColumnDesc();
                              }}
                            />
                            <button
                              onClick={saveColumnDescription}
                              disabled={savingColumnDesc}
                              className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                              title="Save"
                            >
                              <CheckIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={cancelEditingColumnDesc}
                              className="p-1 text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-700 rounded"
                              title="Cancel"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 group">
                            <span className="flex-1">
                              {aiContext?.columnDescriptions?.[col.name] || <span className="text-surface-400 italic">No description</span>}
                            </span>
                            <button
                              onClick={() => startEditingColumnDesc(col.name, aiContext?.columnDescriptions?.[col.name])}
                              className="p-1 text-surface-400 hover:text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Edit description"
                            >
                              <PencilIcon className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Data Preview */}
          {showDataPreview && (
            <div className="card">
              <div className="p-4 border-b border-surface-200 dark:border-surface-800 flex items-center justify-between">
                <h2 className="font-medium text-surface-900 dark:text-white flex items-center gap-2">
                  <EyeIcon className="w-5 h-5" />
                  Data Preview
                  {previewData?.data?.totalRows && (
                    <span className="text-surface-500 font-normal">
                      ({previewData.data.totalRows.toLocaleString()} total rows)
                    </span>
                  )}
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPreviewOffset(Math.max(0, previewOffset - previewLimit))}
                    disabled={previewOffset === 0 || isLoadingPreview}
                    className="btn-secondary text-sm disabled:opacity-50"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-surface-600 dark:text-surface-400">
                    {previewOffset + 1} - {Math.min(previewOffset + previewLimit, previewData?.data?.totalRows || previewOffset + previewLimit)}
                  </span>
                  <button
                    onClick={() => setPreviewOffset(previewOffset + previewLimit)}
                    disabled={!previewData?.data?.hasMore || isLoadingPreview}
                    className="btn-secondary text-sm disabled:opacity-50"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {isLoadingPreview ? (
                <div className="flex items-center justify-center py-12">
                  <ArrowPathIcon className="w-8 h-8 animate-spin text-primary-500" />
                </div>
              ) : previewData?.data?.data?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-surface-50 dark:bg-surface-800 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-surface-500 text-xs">#</th>
                        {previewData.data.columns?.map((col, i) => (
                          <th key={i} className="px-3 py-2 text-left font-medium text-surface-600 dark:text-surface-400 whitespace-nowrap">
                            {col.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.data.data.map((row, rowIndex) => (
                        <tr key={rowIndex} className="border-b border-surface-100 dark:border-surface-800 hover:bg-surface-50 dark:hover:bg-surface-800/50">
                          <td className="px-3 py-2 text-surface-400 text-xs">
                            {previewOffset + rowIndex + 1}
                          </td>
                          {previewData.data.columns?.map((col, colIndex) => (
                            <td key={colIndex} className="px-3 py-2 text-surface-900 dark:text-white max-w-xs truncate" title={String(row[col.name] ?? '')}>
                              {formatCellValue(row[col.name])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-12 text-center text-surface-500">
                  No data available
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Details sidebar */}
        <div className="space-y-6">
          <div className="card p-4">
            <h3 className="font-medium text-surface-900 dark:text-white mb-4">Details</h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-surface-500">Type</dt>
                <dd className="text-surface-900 dark:text-white">{dataSource.type.toUpperCase()}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-surface-500">Status</dt>
                <dd>
                  <span className={`badge ${
                    dataSource.status === 'ready' ? 'badge-success' : 
                    dataSource.status === 'error' ? 'badge-danger' : 'badge-warning'
                  }`}>
                    {dataSource.status}
                  </span>
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-surface-500">Created</dt>
                <dd className="text-surface-900 dark:text-white">
                  {new Date(dataSource.createdAt).toLocaleDateString()}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-surface-500">Last Import</dt>
                <dd className="text-surface-900 dark:text-white">
                  {dataSource.stats?.lastImportedAt 
                    ? new Date(dataSource.stats.lastImportedAt).toLocaleString()
                    : 'Never'}
                </dd>
              </div>
              {dataSource.stats?.lastImportRows != null && (
                <div className="flex justify-between">
                  <dt className="text-surface-500">Rows Imported</dt>
                  <dd className="text-surface-900 dark:text-white">
                    {dataSource.stats.lastImportRows.toLocaleString()}
                  </dd>
                </div>
              )}
            </dl>
          </div>
          
          {/* Web Scraper Settings Card */}
          {dataSource.type === 'webscraper' && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-surface-900 dark:text-white flex items-center gap-2">
                  <Cog6ToothIcon className="w-5 h-5" />
                  Scraper Settings
                </h3>
                {!isEditingWebScraper ? (
                  <button
                    onClick={handleStartEditWebScraper}
                    className="text-primary-600 dark:text-primary-400 text-sm hover:underline"
                  >
                    Edit
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSaveWebScraper}
                      disabled={savingWebScraper}
                      className="text-emerald-600 dark:text-emerald-400 text-sm hover:underline"
                    >
                      {savingWebScraper ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => setIsEditingWebScraper(false)}
                      className="text-surface-500 text-sm hover:underline"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              
              {!isEditingWebScraper ? (
                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="text-surface-500 mb-0.5">URL</dt>
                    <dd className="text-surface-900 dark:text-white break-all">
                      {dataSource.connectionConfig?.scrapeUrl || '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-surface-500 mb-0.5">Extraction Prompt</dt>
                    <dd className="text-surface-900 dark:text-white text-xs leading-relaxed">
                      {dataSource.connectionConfig?.scrapePrompt || '—'}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-surface-500">Strategy</dt>
                    <dd className="text-surface-900 dark:text-white capitalize">
                      {(dataSource.connectionConfig?.paginationStrategy || 'single').replace('_', ' ')}
                    </dd>
                  </div>
                  {dataSource.connectionConfig?.paginationStrategy !== 'single' && (
                    <div className="flex justify-between">
                      <dt className="text-surface-500">
                        {dataSource.connectionConfig?.paginationStrategy === 'infinite_scroll' ? 'Scroll Depth' : 'Pages'}
                      </dt>
                      <dd className="text-surface-900 dark:text-white">
                        {dataSource.connectionConfig?.paginationDepth || 1}
                      </dd>
                    </div>
                  )}
                  {dataSource.connectionConfig?.paginationStrategy === 'url_pattern' && dataSource.connectionConfig?.urlPattern && (
                    <div>
                      <dt className="text-surface-500 mb-0.5">URL Pattern</dt>
                      <dd className="text-surface-900 dark:text-white text-xs font-mono break-all">
                        {dataSource.connectionConfig.urlPattern}
                      </dd>
                    </div>
                  )}
                </dl>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-surface-500 mb-1">URL</label>
                    <input
                      type="url"
                      value={editScrapeUrl}
                      onChange={(e) => setEditScrapeUrl(e.target.value)}
                      className="input w-full text-sm"
                      placeholder="https://example.com/data"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-surface-500 mb-1">Extraction Prompt</label>
                    <textarea
                      value={editScrapePrompt}
                      onChange={(e) => setEditScrapePrompt(e.target.value)}
                      className="input w-full text-sm"
                      rows={3}
                      maxLength={2000}
                      placeholder="Describe what data to extract..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-surface-500 mb-1">Strategy</label>
                    <select
                      value={editPaginationStrategy}
                      onChange={(e) => setEditPaginationStrategy(e.target.value)}
                      className="input w-full text-sm"
                    >
                      <option value="single">Single Page</option>
                      <option value="infinite_scroll">Infinite Scroll</option>
                      <option value="url_pattern">URL Pattern</option>
                    </select>
                  </div>
                  {editPaginationStrategy !== 'single' && (
                    <div>
                      <label className="block text-xs font-medium text-surface-500 mb-1">
                        {editPaginationStrategy === 'infinite_scroll' ? 'Scroll Depth' : 'Number of Pages'} (max 5)
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={5}
                        value={editPaginationDepth}
                        onChange={(e) => setEditPaginationDepth(Math.min(5, Math.max(1, parseInt(e.target.value) || 1)))}
                        className="input w-20 text-sm"
                      />
                    </div>
                  )}
                  {editPaginationStrategy === 'url_pattern' && (
                    <div>
                      <label className="block text-xs font-medium text-surface-500 mb-1">URL Pattern</label>
                      <input
                        type="text"
                        value={editUrlPattern}
                        onChange={(e) => setEditUrlPattern(e.target.value)}
                        className="input w-full text-sm"
                        placeholder="https://example.com/page/{n}"
                      />
                      <p className="text-xs text-surface-400 mt-1">Use {'{n}'} for the page number</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* AI Research Settings Card */}
          {dataSource.type === 'airesearch' && (
            <>
              {/* AI-Synthesized Data Warning Banner */}
              <div className="card p-4 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 overflow-hidden">
                <div className="flex items-start gap-3 min-w-0">
                  <SparklesIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-amber-900 dark:text-amber-100 mb-1">
                      AI-Synthesized Data
                    </h3>
                    <p className="text-sm text-amber-800 dark:text-amber-200 mb-3 break-words">
                      AI can hallucinate. It is your job to ensure that the data our AI generated is accurate. Here is the list of links that the AI used to generate your data. Please review them.
                    </p>
                    {dataSource.connectionConfig?.citations && dataSource.connectionConfig.citations.length > 0 && (
                      <div className="min-w-0 space-y-1">
                        <p className="text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">
                          Sources ({dataSource.connectionConfig.citations.length}):
                        </p>
                        {dataSource.connectionConfig.citations.map((url, i) => (
                          <a
                            key={i}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-xs text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 hover:underline truncate"
                          >
                            {url}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Research Details */}
              <div className="card p-4">
                <h3 className="font-medium text-surface-900 dark:text-white flex items-center gap-2 mb-4">
                  <Cog6ToothIcon className="w-5 h-5" />
                  Research Settings
                </h3>
                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="text-surface-500 mb-0.5">Research Prompt</dt>
                    <dd className="text-surface-900 dark:text-white text-xs leading-relaxed">
                      {dataSource.connectionConfig?.researchPrompt || '—'}
                    </dd>
                  </div>
                  {dataSource.connectionConfig?.researchColumns && dataSource.connectionConfig.researchColumns.length > 0 && (
                    <div>
                      <dt className="text-surface-500 mb-0.5">Defined Columns</dt>
                      <dd className="flex flex-wrap gap-1.5 mt-1">
                        {dataSource.connectionConfig.researchColumns.map((col, i) => (
                          <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-surface-100 dark:bg-surface-800 text-surface-700 dark:text-surface-300">
                            {col.name} <span className="ml-1 text-surface-400">({col.type || 'STRING'})</span>
                          </span>
                        ))}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </>
          )}
          
          {/* Sync Schedule Card - only for external sources (not CSV/Excel or AI Research) */}
          {!isFileSource && !isAiResearch && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-surface-900 dark:text-white flex items-center gap-2">
                  <CalendarDaysIcon className="w-5 h-5" />
                  Sync Schedule
                </h3>
                <button
                  onClick={() => setShowScheduleEditor(true)}
                  className="text-primary-600 dark:text-primary-400 text-sm hover:underline"
                >
                  Edit
                </button>
              </div>
              {dataSource.schedule?.enabled && dataSource.schedule?.frequency !== 'manual' ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                      Scheduled
                    </span>
                    <span className="text-sm text-surface-600 dark:text-surface-400 capitalize">
                      {dataSource.schedule.frequency}
                    </span>
                  </div>
                  <div className="text-sm text-surface-600 dark:text-surface-400">
                    Mode: <span className="font-medium">{dataSource.schedule.importMode === 'full' ? 'Full Refresh' : 'Incremental'}</span>
                  </div>
                  {dataSource.schedule.nextRunAt && (
                    <div className="text-sm text-surface-600 dark:text-surface-400">
                      Next sync: <span className="font-medium">{new Date(dataSource.schedule.nextRunAt).toLocaleString()}</span>
                      {dataSource.schedule.timezone && (
                        <span className="text-surface-500 ml-1">({dataSource.schedule.timezone.replace(/_/g, ' ')})</span>
                      )}
                    </div>
                  )}
                  {dataSource.schedule.lastRunAt && (
                    <div className="text-sm text-surface-500">
                      Last sync: {new Date(dataSource.schedule.lastRunAt).toLocaleString()}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-surface-500">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-surface-100 text-surface-600 dark:bg-surface-800 dark:text-surface-400 mb-2">
                    Manual Only
                  </span>
                  <p className="mt-2">Sync is only triggered manually. Click "Schedule" to set up automatic syncing.</p>
                </div>
              )}
            </div>
          )}

          {/* File Source Info Card - only for CSV/Excel */}
          {isFileSource && (
            <div className="card p-4">
              <h3 className="font-medium text-surface-900 dark:text-white flex items-center gap-2 mb-4">
                <DocumentTextIcon className="w-5 h-5" />
                File Information
              </h3>
              <div className="space-y-2 text-sm">
                {dataSource.fileInfo?.originalName && (
                  <div className="flex justify-between">
                    <dt className="text-surface-500">File Name</dt>
                    <dd className="text-surface-900 dark:text-white truncate ml-2 max-w-[150px]" title={dataSource.fileInfo.originalName}>
                      {dataSource.fileInfo.originalName}
                    </dd>
                  </div>
                )}
                {dataSource.fileInfo?.uploadedAt && (
                  <div className="flex justify-between">
                    <dt className="text-surface-500">Uploaded</dt>
                    <dd className="text-surface-900 dark:text-white">
                      {new Date(dataSource.fileInfo.uploadedAt).toLocaleDateString()}
                    </dd>
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-surface-200 dark:border-surface-700">
                  <button
                    onClick={() => setShowReuploadDialog(true)}
                    className="btn-secondary text-sm w-full"
                  >
                    <CloudArrowUpIcon className="w-4 h-4 mr-2" />
                    Upload New File
                  </button>
                  <p className="text-surface-500 text-xs mt-2">
                    Upload a new file to replace or add to the existing data.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Incremental Sync State Card - only for API sources with incremental sync */}
          {dataSource.type === 'api' && dataSource.connectionConfig?.incrementalConfig?.enabled && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-surface-900 dark:text-white flex items-center gap-2">
                  <ArrowPathIcon className="w-5 h-5" />
                  Incremental Sync State
                </h3>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                  Active
                </span>
              </div>
              <div className="space-y-3 text-sm">
                {/* Strategy Info */}
                <div className="flex justify-between items-start">
                  <dt className="text-surface-500">Strategy</dt>
                  <dd className="text-surface-900 dark:text-white text-right">
                    <span className="font-medium">
                      {({
                        query: 'Query Parameter',
                        path: 'Path Parameter',
                        header: 'Header-based',
                        body: 'Body Parameter',
                        cursor: 'Cursor + State',
                        state: 'State Tracking'
                      })[dataSource.connectionConfig.incrementalConfig.strategy] || 'Query Parameter'}
                    </span>
                  </dd>
                </div>
                
                {/* Tracking Column */}
                <div className="flex justify-between items-start">
                  <dt className="text-surface-500">Tracking Column</dt>
                  <dd className="text-surface-900 dark:text-white font-mono text-xs bg-surface-100 dark:bg-surface-800 px-2 py-0.5 rounded">
                    {dataSource.connectionConfig.incrementalConfig.column || 'Not set'}
                  </dd>
                </div>
                
                {/* Last Synced Value */}
                <div className="pt-2 border-t border-surface-200 dark:border-surface-700">
                  <div className="flex justify-between items-start mb-2">
                    <dt className="text-surface-500">Last Synced Value</dt>
                    <dd className="text-surface-900 dark:text-white font-mono text-xs">
                      {dataSource.connectionConfig.incrementalConfig.lastValue 
                        ? String(dataSource.connectionConfig.incrementalConfig.lastValue)
                        : <span className="text-surface-400 italic">Not synced yet</span>
                      }
                    </dd>
                  </div>
                  {dataSource.connectionConfig.incrementalConfig.lastSyncAt && (
                    <div className="flex justify-between items-start">
                      <dt className="text-surface-500">Last Sync Time</dt>
                      <dd className="text-surface-700 dark:text-surface-300 text-xs">
                        {new Date(dataSource.connectionConfig.incrementalConfig.lastSyncAt).toLocaleString()}
                      </dd>
                    </div>
                  )}
                </div>
                
                {/* Next sync preview */}
                {dataSource.connectionConfig.incrementalConfig.lastValue && (
                  <div className="mt-2 p-2 bg-surface-50 dark:bg-surface-800 rounded text-xs">
                    <p className="text-surface-500 mb-1">Next sync will fetch records where:</p>
                    <code className="text-primary-600 dark:text-primary-400">
                      {dataSource.connectionConfig.incrementalConfig.column} &gt; {String(dataSource.connectionConfig.incrementalConfig.lastValue)}
                    </code>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Data Quality Card - only for database/API sources */}
          {!isFileSource && dataSource.stats?.totalRows > 0 && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-surface-900 dark:text-white flex items-center gap-2">
                  <TableCellsIcon className="w-5 h-5" />
                  Data Quality
                </h3>
              </div>
              <div className="space-y-3">
                <div className="text-sm text-surface-600 dark:text-surface-400">
                  {dataSource.connectionConfig?.deduplication?.keyColumns?.length > 0 ? (
                    <>
                      <p className="flex items-center gap-2">
                        <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        Deduplication configured
                      </p>
                      <p className="text-xs text-surface-500 mt-1">
                        Keys: {dataSource.connectionConfig.deduplication.keyColumns.join(', ')}
                      </p>
                    </>
                  ) : (
                    <p className="flex items-center gap-2">
                      <ExclamationCircleIcon className="w-4 h-4 text-amber-500" />
                      No deduplication keys configured
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    // Pre-populate with existing keys if available
                    setDeduplicateKeyColumns(dataSource.connectionConfig?.deduplication?.keyColumns || []);
                    setShowDeduplicateModal(true);
                  }}
                  className="btn-secondary text-sm w-full"
                >
                  <ArrowPathIcon className="w-4 h-4 mr-2" />
                  Deduplicate Now
                </button>
                <p className="text-xs text-surface-500">
                  Remove duplicate rows from your BigQuery table based on unique key columns.
                </p>
              </div>
            </div>
          )}
          
          {/* How AI Uses This */}
          <div className="card p-4 bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800">
            <h3 className="font-medium text-primary-900 dark:text-primary-100 mb-2 flex items-center gap-2">
              <SparklesIcon className="w-5 h-5 text-primary-500" />
              How AI Uses This
            </h3>
            <p className="text-sm text-primary-700 dark:text-primary-300">
              When you ask the AI assistant to analyze data or create charts, it uses the description, tags, and AI context to find the right data source automatically.
            </p>
            <p className="text-sm text-primary-700 dark:text-primary-300 mt-2">
              {aiContext?.generatedDescription 
                ? '✓ AI description is ready'
                : '⚠ Generate an AI description for better results'
              }
            </p>
          </div>
        </div>
      </div>
      
      {/* Schedule Editor Modal */}
      {showScheduleEditor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-surface-900 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-surface-200 dark:border-surface-700">
              <h2 className="text-xl font-bold text-surface-900 dark:text-white flex items-center gap-2">
                <Cog6ToothIcon className="w-6 h-6" />
                Configure Sync Schedule
              </h2>
              <p className="text-sm text-surface-600 dark:text-surface-400 mt-1">
                Set up automatic data synchronization for {dataSource.name}
              </p>
            </div>
            <div className="p-6">
              <SyncScheduleEditor
                schedule={dataSource.schedule}
                companyTimezone={companyTimezone}
                companyPlan={companyPlan}
                columns={schema?.tables?.[0]?.columns || []}
                incrementalConfig={dataSource.connectionConfig?.incrementalConfig}
                deduplicationConfig={dataSource.connectionConfig?.deduplication}
                dataSourceType={dataSource.type}
                onSave={(schedule) => scheduleMutation.mutate(schedule)}
                onCancel={() => setShowScheduleEditor(false)}
                isSaving={scheduleMutation.isPending}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Schema Editor Modal */}
      {showSchemaEditor && (
        <SchemaEditor
          dataSource={dataSource}
          currentSchema={schema}
          onSave={(updates) => schemaMutation.mutate(updates)}
          onClose={() => setShowSchemaEditor(false)}
          isSaving={schemaMutation.isPending}
        />
      )}
      
      {/* AI Research Regeneration Confirmation Modal */}
      {showAiResearchConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-surface-900 rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                <ExclamationCircleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-surface-900 dark:text-white">
                  Regenerate AI Data?
                </h3>
                <p className="text-sm text-surface-600 dark:text-surface-400 mt-1">
                  This will <strong>overwrite all existing data</strong> in this data source. The AI will search the web again and the results will likely be different from what you currently have.
                </p>
              </div>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-5">
              <p className="text-xs text-amber-800 dark:text-amber-200">
                <strong>This costs 10 credits.</strong> The new dataset may have different rows, different values, or a different number of results. Your current data cannot be recovered after regeneration.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowAiResearchConfirm(false)}
                className="btn bg-surface-100 dark:bg-surface-800 text-surface-700 dark:text-surface-300 hover:bg-surface-200 dark:hover:bg-surface-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowAiResearchConfirm(false);
                  importMutation.mutate('full');
                }}
                className="btn bg-amber-600 text-white hover:bg-amber-700"
              >
                Regenerate Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Re-upload File Dialog for File Sources */}
      {showReuploadDialog && (
        <ReuploadFileDialog
          dataSource={dataSource}
          isOpen={showReuploadDialog}
          onClose={() => {
            setShowReuploadDialog(false);
          }}
          onSuccess={(importId) => {
            console.log('[DataSourceDetail] Re-upload success, import ID:', importId);
            setShowReuploadDialog(false);
            // Start tracking the import progress
            if (importId) {
              console.log('[DataSourceDetail] Setting up progress tracking for import:', importId);
              setCurrentImportId(importId);
              setSyncProgress({
                status: 'running',
                phase: 'uploading',
                percentage: 5,
                message: 'Processing uploaded file...'
              });
            }
            refetch();
            queryClient.invalidateQueries(['dataSourceSchema', id]);
          }}
        />
      )}
      
      {/* Deduplicate Modal */}
      {showDeduplicateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-surface-900 rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-surface-200 dark:border-surface-700">
              <h2 className="text-xl font-bold text-surface-900 dark:text-white flex items-center gap-2">
                <TableCellsIcon className="w-6 h-6" />
                Deduplicate Data
              </h2>
              <p className="text-sm text-surface-600 dark:text-surface-400 mt-1">
                Remove duplicate rows from {dataSource.name}
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  Unique Key Column(s)
                </label>
                <p className="text-sm text-surface-500 mb-3">
                  Select the column(s) that uniquely identify each record. Rows with the same values in these columns will be considered duplicates.
                </p>
                
                {schema?.tables?.[0]?.columns?.length > 0 ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2 p-3 border border-surface-200 dark:border-surface-700 rounded-lg min-h-[42px]">
                      {deduplicateKeyColumns.length === 0 ? (
                        <span className="text-surface-400 text-sm">Click columns below to select unique keys...</span>
                      ) : (
                        deduplicateKeyColumns.map(col => (
                          <span 
                            key={col}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-md text-sm"
                          >
                            {col}
                            <button
                              type="button"
                              onClick={() => setDeduplicateKeyColumns(prev => prev.filter(c => c !== col))}
                              className="hover:text-primary-900 dark:hover:text-primary-100"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </span>
                        ))
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                      {schema.tables[0].columns.map(col => {
                        const isSelected = deduplicateKeyColumns.includes(col.name);
                        return (
                          <button
                            key={col.name}
                            type="button"
                            onClick={() => {
                              setDeduplicateKeyColumns(prev => 
                                isSelected 
                                  ? prev.filter(c => c !== col.name)
                                  : [...prev, col.name]
                              );
                            }}
                            className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                              isSelected
                                ? 'bg-primary-500 text-white border-primary-500'
                                : 'bg-surface-100 dark:bg-surface-800 text-surface-700 dark:text-surface-300 border-surface-300 dark:border-surface-600 hover:border-primary-400'
                            }`}
                          >
                            {col.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={deduplicateKeyColumns.join(', ')}
                    onChange={(e) => setDeduplicateKeyColumns(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                    className="input font-mono text-sm"
                    placeholder="_id or id"
                  />
                )}
              </div>
              
              {deduplicateKeyColumns.length > 0 && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    <strong>Warning:</strong> This operation will permanently remove duplicate rows from your BigQuery table. 
                    For each set of duplicates, only one row will be kept (the most recent if a timestamp column exists).
                  </p>
                </div>
              )}
              
              <div className="text-sm text-surface-500">
                <p><strong>Current stats:</strong></p>
                <ul className="list-disc list-inside mt-1">
                  <li>Total rows: {dataSource.stats?.totalRows?.toLocaleString() || 0}</li>
                </ul>
              </div>
            </div>
            
            <div className="p-6 border-t border-surface-200 dark:border-surface-700 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeduplicateModal(false);
                  setDeduplicateKeyColumns([]);
                }}
                className="btn-secondary"
                disabled={deduplicateMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deduplicateMutation.mutate(deduplicateKeyColumns)}
                className="btn-primary"
                disabled={deduplicateKeyColumns.length === 0 || deduplicateMutation.isPending}
              >
                {deduplicateMutation.isPending ? (
                  <>
                    <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                    Deduplicating...
                  </>
                ) : (
                  <>
                    <CheckIcon className="w-5 h-5 mr-2" />
                    Deduplicate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Re-upload File Dialog Component
function ReuploadFileDialog({ dataSource, isOpen, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [importMode, setImportMode] = useState('full');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadPercentage, setUploadPercentage] = useState(0);
  
  // Sheet selection states for Excel files
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [isCheckingSheets, setIsCheckingSheets] = useState(false);
  
  const acceptedTypes = dataSource?.type === 'csv' 
    ? { 'text/csv': ['.csv'] }
    : { 
        'application/vnd.ms-excel': ['.xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
      };
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async (acceptedFiles) => {
      const uploadedFile = acceptedFiles[0];
      if (uploadedFile) {
        setFile(uploadedFile);
        setSheets([]);
        setSelectedSheet(null);
        
        // For Excel files, check if there are multiple sheets
        if (dataSource?.type === 'excel') {
          setIsCheckingSheets(true);
          try {
            const formData = new FormData();
            formData.append('file', uploadedFile);
            formData.append('mode', importMode);
            
            // Make a request to check for sheets
            const response = await api.post(`/data-sources/${dataSource._id}/reupload`, formData, {
              headers: { 'Content-Type': undefined }
            });
            
            // If backend returns sheet selection requirement
            if (response.data.requiresSheetSelection) {
              setSheets(response.data.sheets);
              // Pre-select the first sheet with data
              const firstWithData = response.data.sheets.find(s => s.hasData) || response.data.sheets[0];
              setSelectedSheet(firstWithData?.name);
            }
          } catch (error) {
            // If it's not a sheet selection response, that's fine - single sheet file
            console.log('Sheet check completed:', error.response?.data || 'Single sheet or error');
          } finally {
            setIsCheckingSheets(false);
          }
        }
      }
    },
    accept: acceptedTypes,
    maxFiles: 1,
    maxSize: 100 * 1024 * 1024 // 100MB
  });
  
  const handleUploadAndImport = async () => {
    if (!file || !dataSource?._id) return;
    
    // For Excel with multiple sheets, require sheet selection
    if (sheets.length > 1 && !selectedSheet) {
      toast.error('Please select a sheet to import');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress('Uploading file...');
    setUploadPercentage(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', importMode);
      if (selectedSheet) {
        formData.append('sheetName', selectedSheet);
      }
      
      const response = await api.post(`/data-sources/${dataSource._id}/reupload`, formData, {
        headers: { 'Content-Type': undefined },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadPercentage(percentCompleted);
          setUploadProgress(`Uploading file... ${percentCompleted}%`);
        }
      });
      
      // Check if this is a sheet selection response (shouldn't happen if we already selected)
      if (response.data.requiresSheetSelection) {
        setSheets(response.data.sheets);
        const firstWithData = response.data.sheets.find(s => s.hasData) || response.data.sheets[0];
        setSelectedSheet(firstWithData?.name);
        setIsUploading(false);
        setUploadProgress(null);
        setUploadPercentage(0);
        return;
      }
      
      setUploadProgress('Processing file and starting import...');
      setUploadPercentage(100);
      
      // Pass the import ID to parent for progress tracking
      const importId = response.data?.import?._id;
      console.log('[ReuploadFileDialog] Import started with ID:', importId);
      
      if (importId) {
        toast.success('File uploaded! Import started.');
        onSuccess(importId);
      } else {
        console.error('[ReuploadFileDialog] No import ID in response:', response.data);
        toast.error('Import started but could not track progress');
        onSuccess(null);
      }
    } catch (error) {
      console.error('Re-upload error:', error);
      toast.error(error.response?.data?.error || 'Failed to upload file');
      setIsUploading(false);
      setUploadProgress(null);
      setUploadPercentage(0);
    }
    // Note: Don't reset uploading state in finally - let the parent handle the transition
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-surface-900 rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-surface-200 dark:border-surface-700">
          <h2 className="text-xl font-bold text-surface-900 dark:text-white flex items-center gap-2">
            <CloudArrowUpIcon className="w-6 h-6" />
            Upload New File
          </h2>
          <p className="text-sm text-surface-600 dark:text-surface-400 mt-1">
            Upload a new {dataSource?.type?.toUpperCase()} file to update the data
          </p>
        </div>
        
        <div className="p-6 space-y-4">
          {/* File Drop Zone */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              isDragActive 
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-950' 
                : file
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                  : 'border-surface-300 dark:border-surface-700 hover:border-primary-400'
            }`}
          >
            <input {...getInputProps()} />
            
            {isCheckingSheets ? (
              <div>
                <ArrowPathIcon className="w-10 h-10 mx-auto mb-2 text-primary-500 animate-spin" />
                <p className="font-medium text-surface-900 dark:text-white">Analyzing file...</p>
              </div>
            ) : file ? (
              <div>
                <CheckCircleIcon className="w-10 h-10 mx-auto mb-2 text-green-500" />
                <p className="font-medium text-surface-900 dark:text-white">{file.name}</p>
                <p className="text-sm text-surface-500">{formatBytes(file.size)}</p>
                <p className="text-xs text-surface-400 mt-2">Click or drop to change file</p>
              </div>
            ) : (
              <>
                <CloudArrowUpIcon className="w-10 h-10 mx-auto mb-2 text-surface-400" />
                <p className="font-medium text-surface-900 dark:text-white mb-1">
                  {isDragActive ? 'Drop your file here' : 'Drag & drop a file'}
                </p>
                <p className="text-sm text-surface-500">or click to browse</p>
              </>
            )}
          </div>
          
          {/* Sheet Selection for Excel files with multiple sheets */}
          {sheets.length > 1 && (
            <div className="space-y-3">
              <label className="text-sm font-medium text-surface-700 dark:text-surface-300">
                Select Sheet to Import
              </label>
              <p className="text-xs text-surface-500">
                This Excel file contains {sheets.length} sheets. Choose which one to import.
              </p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {sheets.map((sheet) => (
                  <label
                    key={sheet.name}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedSheet === sheet.name
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-950'
                        : 'border-surface-200 dark:border-surface-700 hover:border-primary-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="sheet"
                        value={sheet.name}
                        checked={selectedSheet === sheet.name}
                        onChange={(e) => setSelectedSheet(e.target.value)}
                        className="w-4 h-4 text-primary-600"
                      />
                      <span className="font-medium text-surface-900 dark:text-white text-sm">
                        {sheet.name}
                      </span>
                      {!sheet.hasData && (
                        <span className="text-xs text-amber-600 dark:text-amber-400">(empty)</span>
                      )}
                    </div>
                    <span className="text-xs text-surface-500">
                      {sheet.rowCount.toLocaleString()} rows
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
          
          {/* Import Mode Selection */}
          {file && !isCheckingSheets && (
            <div className="space-y-3">
              <label className="text-sm font-medium text-surface-700 dark:text-surface-300">
                Import Mode
              </label>
              
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:border-primary-500 transition-colors">
                <input
                  type="radio"
                  name="reuploadMode"
                  value="full"
                  checked={importMode === 'full'}
                  onChange={(e) => setImportMode(e.target.value)}
                  className="mt-0.5"
                />
                <div>
                  <div className="font-medium text-surface-900 dark:text-white text-sm">Replace All Data</div>
                  <p className="text-xs text-surface-500">Delete existing data and import the new file</p>
                </div>
              </label>
              
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:border-primary-500 transition-colors">
                <input
                  type="radio"
                  name="reuploadMode"
                  value="upsert"
                  checked={importMode === 'upsert'}
                  onChange={(e) => setImportMode(e.target.value)}
                  className="mt-0.5"
                />
                <div>
                  <div className="font-medium text-surface-900 dark:text-white text-sm">Add to Existing</div>
                  <p className="text-xs text-surface-500">Append new rows from this file to existing data</p>
                </div>
              </label>
            </div>
          )}
          
          {/* Progress indicator */}
          {uploadProgress && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400">
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                {uploadProgress}
              </div>
              {/* Upload progress bar */}
              <div className="w-full h-2 bg-surface-200 dark:bg-surface-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary-500 transition-all duration-300 rounded-full"
                  style={{ width: `${uploadPercentage}%` }}
                />
              </div>
            </div>
          )}
        </div>
        
        <div className="p-6 border-t border-surface-200 dark:border-surface-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isUploading}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleUploadAndImport}
            disabled={!file || isUploading || isCheckingSheets || (sheets.length > 1 && !selectedSheet)}
            className="btn-primary disabled:opacity-50"
          >
            {isUploading && <ArrowPathIcon className="w-4 h-4 animate-spin mr-2" />}
            Upload & Import
          </button>
        </div>
      </div>
    </div>
  );
}

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatCellValue(value) {
  if (value === null || value === undefined) {
    return <span className="text-surface-400 italic">null</span>;
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (typeof value === 'object') {
    return <span className="font-mono text-xs">{JSON.stringify(value)}</span>;
  }
  if (typeof value === 'string' && value.length > 100) {
    return value.substring(0, 100) + '...';
  }
  return String(value);
}
